import type { FC } from "react";
import {
  ActionBarPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
  useMessagePartText,
} from "@assistant-ui/react";
import { Box, IconButton, Typography, styled, useTheme } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReactMarkdown from "react-markdown";

const ThreadRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  flex: 1,
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
}));

const ThreadViewport = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  padding: "16px",
  scrollBehavior: "smooth",
  minHeight: 0,
}));

const WelcomeContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  padding: "24px",
  textAlign: "center",
}));

const MessageContainer = styled(Box)<{ messageRole: "user" | "assistant" }>(({ theme, messageRole }) => ({
  display: "flex",
  flexDirection: "column",
  maxWidth: messageRole === "assistant" ? "90%" : "80%",
  padding: "12px 16px",
  borderRadius: "12px",
  marginBottom: "12px",
  alignSelf: messageRole === "user" ? "flex-end" : "flex-start",
  backgroundColor: messageRole === "user" ? theme.palette.primary.main : theme.palette.background.default,
  color: messageRole === "user" ? "#fff" : theme.palette.text.primary,
  fontSize: "14px",
  lineHeight: 1.5,
  // Markdown styling for assistant messages
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    margin: "12px 0 6px 0",
    fontWeight: 600,
    lineHeight: 1.3,
    "&:first-of-type": {
      marginTop: 0,
    },
  },
  "& h1": {
    fontSize: "1.25rem",
  },
  "& h2": {
    fontSize: "1.125rem",
  },
  "& h3": {
    fontSize: "1rem",
  },
  "& p": {
    margin: "6px 0",
    "&:first-of-type": {
      marginTop: 0,
    },
    "&:last-of-type": {
      marginBottom: 0,
    },
  },
  "& ul, & ol": {
    margin: "6px 0",
    paddingLeft: "24px",
  },
  "& li": {
    margin: "4px 0",
    paddingLeft: "4px",
    "& > p": {
      margin: 0,
    },
  },
  "& strong, & b": {
    fontWeight: 600,
  },
  "& em, & i": {
    fontStyle: "italic",
  },
  "& code": {
    backgroundColor: messageRole === "user" ? "rgba(255, 255, 255, 0.15)" : theme.palette.action.hover,
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "0.875em",
    fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace",
  },
  "& pre": {
    backgroundColor: messageRole === "user" ? "rgba(255, 255, 255, 0.1)" : theme.palette.grey[100],
    padding: "12px",
    borderRadius: "8px",
    overflow: "auto",
    margin: "8px 0",
    "& code": {
      backgroundColor: "transparent",
      padding: 0,
    },
  },
  "& a": {
    color: messageRole === "user" ? "#fff" : theme.palette.primary.main,
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "none",
    },
  },
  "& blockquote": {
    borderLeft: `3px solid ${theme.palette.divider}`,
    margin: "8px 0",
    paddingLeft: "12px",
    color: theme.palette.text.secondary,
  },
  "& hr": {
    border: "none",
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: "12px 0",
  },
  "& table": {
    borderCollapse: "collapse",
    width: "100%",
    margin: "8px 0",
    fontSize: "0.875em",
  },
  "& th, & td": {
    border: `1px solid ${theme.palette.divider}`,
    padding: "6px 10px",
    textAlign: "left",
  },
  "& th": {
    backgroundColor: theme.palette.action.hover,
    fontWeight: 600,
  },
}));

const ComposerRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: "8px",
  padding: "12px 16px",
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
}));

const ComposerInput = styled("textarea")(({ theme }) => ({
  flex: 1,
  minHeight: "40px",
  maxHeight: "120px",
  padding: "10px 14px",
  fontSize: "14px",
  fontFamily: "inherit",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  resize: "none",
  outline: "none",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  "&:focus": {
    borderColor: theme.palette.primary.main,
  },
  "&::placeholder": {
    color: theme.palette.text.secondary,
  },
}));

const ActionBarContainer = styled(Box)(() => ({
  display: "flex",
  gap: "4px",
  marginTop: "4px",
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "12px 16px",
  maxWidth: "80px",
  borderRadius: "12px",
  marginBottom: "12px",
  alignSelf: "flex-start",
  backgroundColor: theme.palette.background.default,
  "& .dot": {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: theme.palette.text.secondary,
    animation: "typing 1.4s infinite ease-in-out both",
    "&:nth-of-type(1)": {
      animationDelay: "0s",
    },
    "&:nth-of-type(2)": {
      animationDelay: "0.2s",
    },
    "&:nth-of-type(3)": {
      animationDelay: "0.4s",
    },
  },
  "@keyframes typing": {
    "0%, 80%, 100%": {
      transform: "scale(0.6)",
      opacity: 0.4,
    },
    "40%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
}));

// Thread Welcome Component
const ThreadWelcome: FC = () => (
  <WelcomeContainer>
    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
      OpsHelm Assistant
    </Typography>
    <Typography variant="body2" color="text.secondary">
      How can I help you today?
    </Typography>
  </WelcomeContainer>
);

const UserMessage: FC = () => {
  const message = useMessage();
  const hasContent =
    message.content && message.content.some((part) => part.type === "text" && part.text && part.text.trim().length > 0);

  if (!hasContent) return null;

  return (
    <MessagePrimitive.Root>
      <MessageContainer messageRole="user">
        <MessagePrimitive.Content />
      </MessageContainer>
    </MessagePrimitive.Root>
  );
};

const MarkdownText: FC = () => {
  const { text } = useMessagePartText();
  return <ReactMarkdown>{text}</ReactMarkdown>;
};

const AssistantMessage: FC = () => {
  const message = useMessage();
  const hasContent =
    message.content && message.content.some((part) => part.type === "text" && part.text && part.text.trim().length > 0);

  if (!hasContent) return null;

  return (
    <MessagePrimitive.Root>
      <MessageContainer messageRole="assistant">
        <MessagePrimitive.Content components={{ Text: MarkdownText }} />
        <ActionBarContainer>
          <ActionBarPrimitive.Root hideWhenRunning>
            <ActionBarPrimitive.Copy asChild>
              <IconButton size="small" color="inherit">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </ActionBarPrimitive.Copy>
            <ActionBarPrimitive.Reload asChild>
              <IconButton size="small" color="inherit">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </ActionBarPrimitive.Reload>
          </ActionBarPrimitive.Root>
        </ActionBarContainer>
      </MessageContainer>
    </MessagePrimitive.Root>
  );
};

const LoadingIndicator: FC = () => (
  <TypingIndicator>
    <span className="dot" />
    <span className="dot" />
    <span className="dot" />
  </TypingIndicator>
);

// Composer Component
const Composer: FC = () => {
  const theme = useTheme();
  return (
    <ComposerPrimitive.Root>
      <ComposerRoot>
        <ComposerPrimitive.Input asChild placeholder="Ask OpsHelm assistant..." autoFocus>
          <ComposerInput />
        </ComposerPrimitive.Input>
        <ThreadPrimitive.If running>
          <ComposerPrimitive.Cancel asChild>
            <IconButton
              size="small"
              sx={{
                backgroundColor: theme.palette.error.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.error.dark,
                },
              }}
            >
              <StopIcon />
            </IconButton>
          </ComposerPrimitive.Cancel>
        </ThreadPrimitive.If>
        <ThreadPrimitive.If running={false}>
          <ComposerPrimitive.Send asChild>
            <IconButton
              size="small"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&:disabled": {
                  backgroundColor: theme.palette.action.disabled,
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </ComposerPrimitive.Send>
        </ThreadPrimitive.If>
      </ComposerRoot>
    </ComposerPrimitive.Root>
  );
};

// Main Thread Component
export const Thread: FC = () => (
  <ThreadPrimitive.Root asChild>
    <ThreadRoot>
      <ThreadPrimitive.Viewport asChild>
        <ThreadViewport>
          <ThreadPrimitive.Empty>
            <ThreadWelcome />
          </ThreadPrimitive.Empty>

          <ThreadPrimitive.Messages
            components={{
              UserMessage,
              AssistantMessage,
            }}
          />

          <ThreadPrimitive.If running>
            <LoadingIndicator />
          </ThreadPrimitive.If>
        </ThreadViewport>
      </ThreadPrimitive.Viewport>
      <Composer />
    </ThreadRoot>
  </ThreadPrimitive.Root>
);

export default Thread;
